/**
 * Billing Claim API Service
 */

import apiClient from './apiClient';
import {
  BillingClaimDTO,
  BillingClaimRequest,
  BillingClaimSettlementDTO,
  ClaimSettlementSummary,
  RecordSettlementRequest,
  BillingClaimSearchParams,
  BillingClaimSummary,
  ClaimSignatureDTO,
  ClaimStatus,
  ClaimType,
  PageResponse,
  IndividualInvoiceRequest,
  IndividualInvoiceDTO,
} from '../types/billing.types';

const BASE_PATH = '/v1/billing-claims';
const INDIVIDUAL_INVOICE_BASE = '/v1/individual-invoices';

class BillingService {
  async createBillingClaim(request: BillingClaimRequest): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(BASE_PATH, request);
    return response.data;
  }

  /** Includes the full claim form (formData). */
  async getBillingClaimById(id: string): Promise<BillingClaimDTO> {
    const response = await apiClient.get<BillingClaimDTO>(`${BASE_PATH}/${id}`);
    return response.data;
  }

  async getBillingClaimByClaimNumber(claimNumber: string): Promise<BillingClaimDTO> {
    const response = await apiClient.get<BillingClaimDTO>(`${BASE_PATH}/claim-number/${claimNumber}`);
    return response.data;
  }

  /** Paginated, filtered list; results omit formData. */
  async searchBillingClaims(params: BillingClaimSearchParams = {}): Promise<PageResponse<BillingClaimDTO>> {
    const response = await apiClient.get<PageResponse<BillingClaimDTO>>(BASE_PATH, {
      params,
      // Spring binds repeated keys (status=A&status=B), not axios' default status[]=A
      paramsSerializer: { indexes: null },
    });
    return response.data;
  }

  async getAllBillingClaims(page: number = 0, size: number = 20): Promise<PageResponse<BillingClaimDTO>> {
    return this.searchBillingClaims({ page, size });
  }

  async getSummary(): Promise<BillingClaimSummary> {
    const response = await apiClient.get<BillingClaimSummary>(`${BASE_PATH}/summary`);
    return response.data;
  }

  async getBillingClaimsByResident(residentId: string): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/resident/${residentId}`);
    return response.data;
  }

  async getBillingClaimsByStatus(status: ClaimStatus): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/status/${status}`);
    return response.data;
  }

  async getBillingClaimsByType(claimType: ClaimType): Promise<BillingClaimDTO[]> {
    const response = await apiClient.get<BillingClaimDTO[]>(`${BASE_PATH}/type/${claimType}`);
    return response.data;
  }

  /** Replaces all editable fields of a draft claim. */
  async updateBillingClaim(id: string, request: BillingClaimRequest): Promise<BillingClaimDTO> {
    const response = await apiClient.put<BillingClaimDTO>(`${BASE_PATH}/${id}`, request);
    return response.data;
  }

  async submitBillingClaim(id: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/submit`);
    return response.data;
  }

  async approveBillingClaim(id: string, settlementData?: Record<string, unknown>): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/approve`, settlementData);
    return response.data;
  }

  async denyBillingClaim(id: string, reason: string, code?: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/deny`, { reason, code });
    return response.data;
  }

  /** "Reject Bill": marks a submitted claim DENIED with the payer's reason. */
  async rejectBillingClaim(id: string, reason: string, code?: string): Promise<BillingClaimDTO> {
    const response = await apiClient.post<BillingClaimDTO>(`${BASE_PATH}/${id}/reject`, { reason, code });
    return response.data;
  }

  // ── Settlements ─────────────────────────────────────────────────────────

  async getSettlementSummary(claimId: string): Promise<ClaimSettlementSummary> {
    const response = await apiClient.get<ClaimSettlementSummary>(`${BASE_PATH}/${claimId}/settlements/summary`);
    return response.data;
  }

  async listSettlements(claimId: string, page = 0, size = 50): Promise<PageResponse<BillingClaimSettlementDTO>> {
    const response = await apiClient.get<PageResponse<BillingClaimSettlementDTO>>(
      `${BASE_PATH}/${claimId}/settlements`,
      { params: { page, size } }
    );
    return response.data;
  }

  async recordSettlement(claimId: string, request: RecordSettlementRequest): Promise<BillingClaimSettlementDTO> {
    const response = await apiClient.post<BillingClaimSettlementDTO>(`${BASE_PATH}/${claimId}/settlements`, request);
    return response.data;
  }

  async voidSettlement(claimId: string, settlementId: string, reason: string): Promise<BillingClaimSettlementDTO> {
    const response = await apiClient.delete<BillingClaimSettlementDTO>(
      `${BASE_PATH}/${claimId}/settlements/${settlementId}`,
      { params: { reason } }
    );
    return response.data;
  }

  /** Receipt PDF for one settlement, with the balance as it was when it was posted. */
  async getSettlementReceipt(claimId: string, settlementId: string): Promise<Blob> {
    const response = await apiClient.get(`${BASE_PATH}/${claimId}/settlements/${settlementId}/receipt`, {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
    });
    return response.data;
  }

  /** Claim-wise settlement report as PDF or XLSX. */
  async getClaimSettlementReport(claimId: string, format: 'PDF' | 'XLSX' = 'PDF'): Promise<Blob> {
    const response = await apiClient.get(`${BASE_PATH}/${claimId}/settlements/report`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }

  /** Resident-wise (default) or facility-wise settlement report as PDF or XLSX. */
  async getSettlementReport(
    filters: Record<string, string | undefined>,
    format: 'PDF' | 'XLSX' = 'PDF'
  ): Promise<Blob> {
    const response = await apiClient.get('/v1/billing-settlements/report', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }

  /** Settlement report as PDF. */
  async getSettlementReportPdf(params: {
    startDate?: string;
    endDate?: string;
    dateBasis?: string;
    [key: string]: string | undefined;
  }): Promise<Blob> {
    const response = await apiClient.get('/v1/billing-settlements/report/pdf', {
      params: { dateBasis: 'SERVICE_DATE', ...params },
      headers: { Accept: 'application/pdf, application/octet-stream, */*' },
      responseType: 'blob',
    });
    return response.data;
  }

  /** Settlement report as Excel (XLSX). */
  async getSettlementReportExcel(params: {
    startDate?: string;
    endDate?: string;
    dateBasis?: string;
    [key: string]: string | undefined;
  }): Promise<Blob> {
    const response = await apiClient.get('/v1/billing-settlements/report/excel', {
      params: { dateBasis: 'SERVICE_DATE', ...params },
      headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*' },
      responseType: 'blob',
    });
    return response.data;
  }

  /** The claim printed on its official CMS-1500 / UB-04 form. */
  async getClaimPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`${BASE_PATH}/${id}/pdf`, {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteBillingClaim(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  /**
   * Fetches official claim PDF as Blob.
   * Calls GET /v1/billing-claims/{id}/pdf
   */
  async getBillingClaimPdf(id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${id}/pdf`, {
      responseType: 'arraybuffer',
      headers: { Accept: 'application/pdf' },
      signal,
    });
    const mimeType = response.headers['content-type'] || 'application/pdf';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Upload patient signature file (Box 12)
   * Calls POST /v1/billing-claims/{id}/patient-signature/upload
   */
  async uploadPatientSignature(
    claimId: string,
    file: File | Blob,
    signatureDate?: string
  ): Promise<ClaimSignatureDTO> {
    const formData = new FormData();
    formData.append('file', file);
    if (signatureDate) {
      formData.append('signatureDate', signatureDate);
    }
    const response = await apiClient.post<ClaimSignatureDTO>(
      `${BASE_PATH}/${claimId}/patient-signature/upload`,
      formData,
      {
        params: signatureDate ? { signatureDate } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  /**
   * Upload insured signature file (Box 13)
   * Calls POST /v1/billing-claims/{id}/insured-signature/upload
   */
  async uploadInsuredSignature(
    claimId: string,
    file: File | Blob,
    signatureDate?: string
  ): Promise<ClaimSignatureDTO> {
    const formData = new FormData();
    formData.append('file', file);
    if (signatureDate) {
      formData.append('signatureDate', signatureDate);
    }
    const response = await apiClient.post<ClaimSignatureDTO>(
      `${BASE_PATH}/${claimId}/insured-signature/upload`,
      formData,
      {
        params: signatureDate ? { signatureDate } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  /**
   * Upload digital canvas base64 signature for patient (Box 12)
   */
  async uploadPatientSignatureBase64(
    claimId: string,
    base64: string,
    signatureDate?: string
  ): Promise<ClaimSignatureDTO> {
    const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
    const response = await apiClient.post<ClaimSignatureDTO>(
      `${BASE_PATH}/${claimId}/patient-signature/upload`,
      null,
      {
        params: {
          base64: cleanBase64,
          ...(signatureDate ? { signatureDate } : {}),
        },
      }
    );
    return response.data;
  }

  /**
   * Upload digital canvas base64 signature for insured (Box 13)
   */
  async uploadInsuredSignatureBase64(
    claimId: string,
    base64: string,
    signatureDate?: string
  ): Promise<ClaimSignatureDTO> {
    const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
    const response = await apiClient.post<ClaimSignatureDTO>(
      `${BASE_PATH}/${claimId}/insured-signature/upload`,
      null,
      {
        params: {
          base64: cleanBase64,
          ...(signatureDate ? { signatureDate } : {}),
        },
      }
    );
    return response.data;
  }

  /**
   * Download patient signature image (Box 12)
   * Calls GET /v1/billing-claims/{id}/patient-signature
   */
  async getPatientSignature(claimId: string): Promise<string | null> {
    try {
      const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${claimId}/patient-signature`, {
        responseType: 'arraybuffer',
      });
      if (response.data && response.data.byteLength > 0) {
        const mimeType = response.headers['content-type'] || 'image/png';
        const blob = new Blob([response.data], { type: mimeType });
        return new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              resolve(null);
            }
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch (err) {
      console.warn(`Could not fetch patient signature for claim ${claimId}:`, err);
      return null;
    }
  }

  /**
   * Download insured signature image (Box 13)
   * Calls GET /v1/billing-claims/{id}/insured-signature
   */
  async getInsuredSignature(claimId: string): Promise<string | null> {
    try {
      const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${claimId}/insured-signature`, {
        responseType: 'arraybuffer',
      });
      if (response.data && response.data.byteLength > 0) {
        const mimeType = response.headers['content-type'] || 'image/png';
        const blob = new Blob([response.data], { type: mimeType });
        return new Promise<string | null>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              resolve(null);
            }
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
      return null;
    } catch (err) {
      console.warn(`Could not fetch insured signature for claim ${claimId}:`, err);
      return null;
    }
  }

  /**
   * Raw blob fetch for patient signature image
   */
  async getPatientSignatureBlob(claimId: string): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${claimId}/patient-signature`, {
      responseType: 'arraybuffer',
    });
    const mimeType = response.headers['content-type'] || 'image/png';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Raw blob fetch for insured signature image
   */
  async getInsuredSignatureBlob(claimId: string): Promise<Blob> {
    const response = await apiClient.get<ArrayBuffer>(`${BASE_PATH}/${claimId}/insured-signature`, {
      responseType: 'arraybuffer',
    });
    const mimeType = response.headers['content-type'] || 'image/png';
    return new Blob([response.data], { type: mimeType });
  }

  /**
   * Remove patient signature (Box 12)
   * Calls DELETE /v1/billing-claims/{id}/patient-signature/remove
   */
  async removePatientSignature(claimId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${claimId}/patient-signature/remove`);
  }

  /**
   * Remove insured signature (Box 13)
   * Calls DELETE /v1/billing-claims/{id}/insured-signature/remove
   */
  async removeInsuredSignature(claimId: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${claimId}/insured-signature/remove`);
  }

  /**
   * Send claim PDF via email
   * Calls POST /v1/billing-claims/{id}/send-to-email
   */
  async sendClaimEmail(
    id: string,
    emails: string | string[]
  ): Promise<void> {
    const emailList = Array.isArray(emails)
      ? emails.map((e) => e.trim()).filter(Boolean)
      : emails
          .split(/[,;]/)
          .map((e) => e.trim())
          .filter(Boolean);

    await apiClient.post(`${BASE_PATH}/${id}/send-to-email`, {
      emails: emailList,
    });
  }

  /**
   * Create an Individual Invoice
   * Calls POST /v1/individual-invoices
   */
  async createIndividualInvoice(request: IndividualInvoiceRequest): Promise<IndividualInvoiceDTO> {
    const response = await apiClient.post<IndividualInvoiceDTO>(INDIVIDUAL_INVOICE_BASE, request);
    return response.data;
  }

  /**
   * Update an Individual Invoice
   * Calls PUT /v1/individual-invoices/{id}
   */
  async updateIndividualInvoice(id: string, request: IndividualInvoiceRequest): Promise<IndividualInvoiceDTO> {
    const response = await apiClient.put<IndividualInvoiceDTO>(`${INDIVIDUAL_INVOICE_BASE}/${id}`, request);
    return response.data;
  }

  /**
   * Finalize an Individual Invoice
   * Calls POST /v1/individual-invoices/{id}/finalize
   */
  async finalizeIndividualInvoice(id: string): Promise<IndividualInvoiceDTO> {
    const response = await apiClient.post<IndividualInvoiceDTO>(`${INDIVIDUAL_INVOICE_BASE}/${id}/finalize`);
    return response.data;
  }

  /**
   * Get an Individual Invoice by ID
   * Calls GET /v1/individual-invoices/{id}
   */
  async getIndividualInvoiceById(id: string): Promise<IndividualInvoiceDTO> {
    const response = await apiClient.get<IndividualInvoiceDTO>(`${INDIVIDUAL_INVOICE_BASE}/${id}`);
    return response.data;
  }

  /**
   * Search and list Individual Invoices
   * Calls GET /v1/individual-invoices?searchTerm=...
   */
  async searchIndividualInvoices(params: {
    searchTerm?: string;
    page?: number;
    size?: number;
    status?: string;
    residentId?: string;
    [key: string]: any;
  } = {}): Promise<PageResponse<IndividualInvoiceDTO> | IndividualInvoiceDTO[]> {
    const cleanParams: Record<string, any> = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        cleanParams[k] = v;
      }
    });
    const response = await apiClient.get<PageResponse<IndividualInvoiceDTO> | IndividualInvoiceDTO[]>(
      INDIVIDUAL_INVOICE_BASE,
      { params: cleanParams }
    );
    return response.data;
  }

  /**
   * Delete an Individual Invoice
   * Calls DELETE /v1/individual-invoices/{id}
   */
  async deleteIndividualInvoice(id: string): Promise<void> {
    const cleanId = encodeURIComponent(String(id).trim());
    await apiClient.delete(`${INDIVIDUAL_INVOICE_BASE}/${cleanId}`);
  }

  /**
   * Get an Individual Invoice PDF as a Blob
   * Calls GET /v1/individual-invoices/{id}/pdf
   */
  async getIndividualInvoicePdf(id: string): Promise<Blob> {
    const cleanId = encodeURIComponent(String(id).trim());
    const response = await apiClient.get<Blob>(`${INDIVIDUAL_INVOICE_BASE}/${cleanId}/pdf`, {
      headers: { Accept: 'application/pdf, application/octet-stream, */*' },
      responseType: 'blob',
    });
    return response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' });
  }

  /**
   * Get previous services used by a resident for individual invoices
   * Calls GET /v1/individual-invoices/resident/{residentId}/previous-services
   */
  async getResidentPreviousServices(residentId: string): Promise<any> {
    const cleanId = encodeURIComponent(String(residentId).trim());
    const response = await apiClient.get<any>(
      `${INDIVIDUAL_INVOICE_BASE}/resident/${cleanId}/previous-services`
    );
    return response.data;
  }
}

export default new BillingService();
